import React, { useRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { setContacts } from "redux/appSlice";
import { isTruthy } from "helpers/methods";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import { getContactSuggestion } from "../../SignRequestHelper";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";

interface MobileContactAutocompleteProps {
  field: "name" | "email";
  id: string;
  label: string;
  placeholder: string;
  value: string;
  index: number;
  currentEmail: string;
  contactList: any;
  setContactList: Function;
  excludeIds: string[];
  setExcludeIds: Function;
  /** Dispatch the appropriate name/email update (signer or reviewer). */
  onUpdate: (fields: { name?: string; email?: string }) => void;
  error?: any;
  errorMessage?: string;
  /** Borderless white variant for use inside tinted containers. */
  filled?: boolean;
}

/**
 * Reusable name/email autocomplete with contact suggestions, styled for the
 * mobile design. Persistence is delegated to the caller via `onUpdate` so it
 * works for both signers (updateIndividualSigner) and approvers
 * (updateIndividualReviewer).
 */
const MobileContactAutocomplete: React.FC<MobileContactAutocompleteProps> = ({
  field,
  id,
  label,
  placeholder,
  value,
  index,
  currentEmail,
  contactList,
  setContactList,
  excludeIds,
  setExcludeIds,
  onUpdate,
  error,
  errorMessage,
  filled,
}) => {
  const theme = useTheme();
  const classes = MobileSignRequestStyles(theme);
  const dispatch = useAppDispatch();
  const contacts = useAppSelector((s: RootState) => s.app.contacts);
  const timeoutRef = useRef<any>(null);

  const trimName = (v: string) =>
    v.trimStart().replace(/(?:^\s+|\s+$|\t+)/g, " ").substring(0, 200);
  const cleanEmail = (v: string) => v.replace(/\s/g, "").substring(0, 256);

  const handleResetContacts = () => {
    if (contacts.length < 10) {
      getContactSuggestion("", dispatch);
      dispatch(setContacts(contacts));
    }
  };

  const options = (
    contactList[field] && contactList[field][index]
      ? contactList[field][index]
      : contacts || []
  )?.filter(
    (c: any) =>
      !excludeIds.map((eid: string) => eid.toLowerCase()).includes(
        c.email.toLowerCase(),
      ),
  );

  return (
    <Box>
      <Typography component="label" htmlFor={id} sx={classes.fieldLabel}>
        {label}
      </Typography>
      <CustomAutoComplete
        id={id}
        placeHolder={placeholder}
        name={field}
        freeSolo
        textFieldVariant="outlined"
        value={value || ""}
        sx={filled ? classes.textInputFilled : classes.textInput}
        onBlur={handleResetContacts}
        onChange={(_e: any, newValue: any) => {
          if (newValue && typeof newValue === "object") {
            const updatedExcludeIds = [
              ...excludeIds.filter((eid) => eid !== currentEmail),
              newValue.email,
            ];
            setExcludeIds(updatedExcludeIds);
            onUpdate({
              name: (newValue.name || "").substring(0, 200),
              email: (newValue.email || "").substring(0, 256),
            });
            getContactSuggestion(" ", dispatch, updatedExcludeIds);
          } else if (typeof newValue === "string") {
            onUpdate({
              [field]: field === "name" ? trimName(newValue) : cleanEmail(newValue),
            });
          } else if (newValue === null || newValue === "") {
            onUpdate({ [field]: "" });
          }
        }}
        onChangeForTextField={(e: React.ChangeEvent<HTMLInputElement>) => {
          const val = e.target.value;
          onUpdate({
            [field]: field === "name" ? trimName(val) : cleanEmail(val),
          });
          clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(async () => {
            if (isTruthy(val.trim())) {
              if (field === "name") dispatch(setContacts([]));
              await getContactSuggestion(
                val.trim(),
                dispatch,
                excludeIds,
                setContactList,
                index,
                field,
              );
            } else if (field === "name") {
              await getContactSuggestion(
                "",
                dispatch,
                excludeIds,
                setContactList,
                index,
                field,
              );
              dispatch(setContacts(contacts));
            } else {
              dispatch(setContacts([]));
            }
          }, 500);
        }}
        filterOptions={(opts: any, { inputValue }: any) =>
          opts.filter(
            (option: any) =>
              option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.email.toLowerCase().includes(inputValue.toLowerCase()),
          )
        }
        ref={timeoutRef}
        options={options}
        getOptionLabel={(option: any) =>
          typeof option === "string"
            ? option
            : (field === "name" ? option.name : option.email) || ""
        }
        renderOption={(prop: any, option: any) => (
          <li {...prop}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {option.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "grey" }}>
                {option.email}
              </Typography>
            </Box>
          </li>
        )}
        error={error}
        errorMessage={errorMessage}
      />
    </Box>
  );
};

export default MobileContactAutocomplete;
